import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/authHeaders';

const UnitTypeList = () => {
    const companyId = localStorage.getItem('selectedCompanyId');
    const authConfig = () => ({ headers: getAuthHeaders() });
    const [unitTypes, setUnitTypes] = useState([]);
    const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
    const [error, setError] = useState(null);

    // Form state
    const [name, setName] = useState('');
    const [rank, setRank] = useState(1);

    // Editing state
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editRank, setEditRank] = useState(1);

    const fetchUnitTypes = async () => {
        try {
            const response = await axios.get(`http://localhost:8000/api/companies/${companyId}/unit-types`, authConfig());
            setUnitTypes(response.data.data);
        } catch (err) {
            setError('Failed to fetch unit types.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUnitTypes();
    }, [companyId]);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`http://localhost:8000/api/companies/${companyId}/unit-types`, {
                name,
                rank
            }, authConfig());
            setName('');
            setRank(1);
            fetchUnitTypes();
            alert('Unit type created successfully!');
        } catch (err) {
            alert('Error creating unit type.');
        }
    };

    const startEdit = (unitType) => {
        setEditingId(unitType.id);
        setEditName(unitType.name);
        setEditRank(unitType.rank);
    };

    const cancelEdit = () => {
        setEditingId(null);
    };

    const saveEdit = async (id) => {
        try {
            await axios.put(`http://localhost:8000/api/companies/${companyId}/unit-types/${id}`, {
                name: editName,
                rank: editRank
            }, authConfig());
            setEditingId(null);
            fetchUnitTypes();
        } catch (err) {
            alert('Error updating unit type.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this unit type? Departments using it will keep their other data but lose this type label.')) return;
        try {
            await axios.delete(`http://localhost:8000/api/companies/${companyId}/unit-types/${id}`, authConfig());
            fetchUnitTypes();
        } catch (err) {
            alert('Error deleting unit type.');
        }
    };

    if (initialLoading) return <div className="p-6 text-center text-gray-600">Loading unit types...</div>;
    if (error) return <div className="p-6 text-center text-red-600">{error}</div>;

    return (
        <div className="max-w-4xl mx-auto p-6 mt-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Organizational Unit Types</h2>
            <p className="text-sm text-gray-500 mb-6">Define the types of units in your org chart (e.g. Division, Department, Unit, Team) and their display order.</p>

            {/* Creation Form */}
            <form onSubmit={handleCreate} className="bg-white p-6 rounded-lg shadow-md mb-8 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Type Name</label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Division"
                        required
                        className="w-full border border-gray-300 rounded-md p-2 text-sm"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rank / Order</label>
                    <input
                        type="number"
                        value={rank}
                        onChange={(e) => setRank(e.target.value)}
                        required
                        className="w-full border border-gray-300 rounded-md p-2 text-sm"
                    />
                </div>
                <div>
                    <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 text-sm font-medium">
                        + Add Unit Type
                    </button>
                </div>
            </form>

            {/* Unit Types Table */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {unitTypes.length === 0 ? (
                            <tr>
                                <td colSpan="3" className="px-6 py-4 text-center text-gray-500">No unit types found. Create your first one above!</td>
                            </tr>
                        ) : (
                            unitTypes.map((ut) => (
                                <tr key={ut.id} className="hover:bg-gray-50">
                                    {editingId === ut.id ? (
                                        <>
                                            <td className="px-6 py-3">
                                                <input
                                                    type="number"
                                                    value={editRank}
                                                    onChange={(e) => setEditRank(e.target.value)}
                                                    className="w-20 border border-gray-300 rounded-md p-1 text-sm"
                                                />
                                            </td>
                                            <td className="px-6 py-3">
                                                <input
                                                    type="text"
                                                    value={editName}
                                                    onChange={(e) => setEditName(e.target.value)}
                                                    className="w-full border border-gray-300 rounded-md p-1 text-sm"
                                                />
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm">
                                                <button onClick={() => saveEdit(ut.id)} className="text-blue-600 font-medium mr-3">Save</button>
                                                <button onClick={cancelEdit} className="text-gray-500">Cancel</button>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{ut.rank}</td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{ut.name}</td>
                                            <td className="px-6 py-3 whitespace-nowrap text-sm">
                                                <button onClick={() => startEdit(ut)} className="text-blue-600 font-medium mr-3">Edit</button>
                                                <button onClick={() => handleDelete(ut.id)} className="text-red-600 font-medium">Delete</button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UnitTypeList;